export interface FacilitiesJson {
  readonly categories: Record<string, readonly Category[]>;
  readonly facilities: readonly Facility[];
}

export interface Category {
  readonly name: string;
  readonly slug: string;
  readonly children?: readonly Category[];
}

export interface Facility {
  readonly id: string;
  readonly link: string;
  readonly name: string;
  readonly lat: number;
  readonly lng: number;
  readonly open: string;
  readonly city: readonly string[];
  readonly type: readonly string[];
  readonly feat: readonly string[];
  readonly line: readonly (string | null)[];
  readonly station: readonly (string | null)[];
}

export interface Filter {
  bounds?: google.maps.LatLngBounds;
  pref?: string | null;
  area?: string | null;
  city?: readonly (string | null | undefined)[];
  line?: readonly (string | null | undefined)[];
  type?: readonly (string | null | undefined)[];
  feat?: readonly (string | null | undefined)[];
}

export const CAT_CITY = '施設情報エリアカテゴリ';
export const CAT_LINE = '沿線';
export const CAT_TYPE = '施設類型';

export const loadFacilities = async (initialFilter?: (facility: Facility) => unknown) => {
  const res = await fetch('/search/facility.json');
  const data = (await res.json()) as FacilitiesJson;
  return initialFilter ? { ...data, facilities: data.facilities.filter(initialFilter) } : data;
};

const inFilter = <T>(filter: Set<T>, values: readonly T[]) => {
  return filter.size === 0 || values.some((value) => filter.has(value));
};

const strcmp = (a: string, b: string) => (a > b ? 1 : a < b ? -1 : 0);

export const filterFacilities = (data: FacilitiesJson, { bounds, pref, area, city, line, type, feat }: Filter) => {
  const scity = new Set(pref && !city?.length ? subCats(data, CAT_CITY, pref) : city);
  const sline = new Set(area && !line?.length ? subCats(data, CAT_LINE, area) : line);
  const stype = new Set(type?.flatMap((type) => subCats(data, CAT_TYPE, type)));
  const sfeat = new Set(feat);

  return data.facilities
    .filter(
      ({ lat, lng, city, line, type, feat }) =>
        (bounds?.contains({ lat, lng }) ?? true) &&
        inFilter(scity, city) &&
        inFilter(sline, line) &&
        inFilter(stype, type) &&
        inFilter(sfeat, feat),
    )
    .sort((a, b) => strcmp(b.open, a.open));
};

export const cat = (data: FacilitiesJson, catName: string, value?: string | null) => {
  return data.categories[catName].find(({ slug }) => slug === value);
};

export const subCats = (data: FacilitiesJson, catName: string, value?: string | null) => {
  return cat(data, catName, value)?.children?.map(({ slug }) => slug) || [];
};

export const getTypeMappings = (data: FacilitiesJson) => {
  return Object.fromEntries(
    data.categories[CAT_TYPE].flatMap(({ slug, children }) => children!.map(({ slug: key }) => [key, slug])),
  );
};
